<template>
  <Dialog v-model="dialogVisible" :title="dialogTitle" width="1280">
    <el-form
      ref="formRef"
      v-loading="formLoading"
      :model="formData"
      :rules="formRules"
      label-width="130px"
    >
      <!-- ========== 第 1 组：基础信息 ========== -->
      <el-row>
        <el-col :span="8">
          <el-form-item label="合同编号" prop="no">
            <el-input disabled v-model="formData.no" placeholder="保存时自动生成" />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="合同名称" prop="name">
            <el-input v-model="formData.name" placeholder="请输入合同名称" />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="负责人" prop="ownerUserId">
            <el-select
              v-model="formData.ownerUserId"
              :disabled="formType !== 'create'"
              class="w-1/1"
            >
              <el-option
                v-for="item in userOptions"
                :key="item.id"
                :label="item.nickname"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="8">
          <el-form-item label="客户名称" prop="customerId">
            <el-select
              v-model="formData.customerId"
              placeholder="请选择客户"
              class="w-1/1"
              @change="handleCustomerChange"
            >
              <el-option
                v-for="item in customerList"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="商机名称" prop="businessId">
            <el-select
              @change="handleBusinessChange"
              :disabled="!formData.customerId"
              v-model="formData.businessId"
              class="w-1/1"
            >
              <el-option
                v-for="item in getBusinessOptions"
                :key="item.id"
                :label="item.name"
                :value="item.id!"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <!-- ★ 原型里「订单编号」是灰底只读（由订单带出）。但当前 crm_order.contract_no
               是字符串、0 行有值，订单与合同还没有数据级关联 ⇒ 暂时做成**可手工填写**的占位字段。
               等订单链路打通后应改成下拉带出。 -->
          <el-form-item label="订单编号" prop="orderNo">
            <el-input v-model="formData.orderNo" placeholder="请输入订单编号" />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="8">
          <el-form-item label="合同类型" prop="contractType">
            <el-select v-model="formData.contractType" placeholder="请选择合同类型" class="w-1/1">
              <el-option
                v-for="dict in getStrDictOptions(DICT_TYPE.CRM_CONTRACT_TYPE)"
                :key="dict.value"
                :label="dict.label"
                :value="dict.value"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="项目名称" prop="projectName">
            <el-input v-model="formData.projectName" placeholder="请输入项目名称" />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <!-- 项目类型刻意用自由文本：原型的「项目管理」是独立模块、其项目类型是字典，
               两边同时建字典会形成两套口径并漂移（决策见 SQL 文件头第 5 条）。 -->
          <el-form-item label="项目类型" prop="projectType">
            <el-input v-model="formData.projectType" placeholder="请输入项目类型" />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="8">
          <!-- ★ 签约日期是**独立字段**，不要与「下单日期」/「开始时间」混用。
               列表的「签约月份」筛选绑的就是它。 -->
          <el-form-item label="签约日期" prop="signDate">
            <el-date-picker
              v-model="formData.signDate"
              placeholder="选择签约日期"
              type="date"
              value-format="x"
              class="!w-1/1"
            />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="到期日期" prop="endTime">
            <el-date-picker
              v-model="formData.endTime"
              placeholder="选择到期日期"
              type="date"
              value-format="x"
              class="!w-1/1"
            />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="下单日期" prop="orderDate">
            <el-date-picker
              v-model="formData.orderDate"
              placeholder="选择下单日期"
              type="date"
              value-format="x"
              class="!w-1/1"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="8">
          <el-form-item label="开始时间" prop="startTime">
            <el-date-picker
              v-model="formData.startTime"
              placeholder="选择开始时间"
              type="date"
              value-format="x"
              class="!w-1/1"
            />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="收/付款" prop="paymentDirection">
            <el-select v-model="formData.paymentDirection" placeholder="请选择" class="w-1/1">
              <el-option
                v-for="dict in getIntDictOptions(DICT_TYPE.CRM_CONTRACT_PAYMENT_DIRECTION)"
                :key="dict.value"
                :label="dict.label"
                :value="dict.value"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="付款方式" prop="paymentMethod">
            <el-select v-model="formData.paymentMethod" placeholder="请选择付款方式" class="w-1/1">
              <el-option
                v-for="dict in getStrDictOptions(DICT_TYPE.CRM_CONTRACT_PAYMENT_METHOD)"
                :key="dict.value"
                :label="dict.label"
                :value="dict.value"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="8">
          <el-form-item label="付款分期" prop="installment">
            <el-select v-model="formData.installment" placeholder="请选择付款分期" class="w-1/1">
              <el-option
                v-for="dict in getStrDictOptions(DICT_TYPE.CRM_CONTRACT_INSTALLMENT)"
                :key="dict.value"
                :label="dict.label"
                :value="dict.value"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="续费说明" prop="renewDesc">
            <el-input v-model="formData.renewDesc" placeholder="请输入续费说明" />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="续费费用" prop="renewFee">
            <el-input-number
              v-model="formData.renewFee"
              placeholder="请输入续费费用"
              controls-position="right"
              :min="0"
              :precision="2"
              class="!w-1/1"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <!-- ========== 第 2 组：合同与附件上传（原型为整行） ========== -->
      <el-row>
        <el-col :span="24">
          <el-form-item label="合同上传" prop="contractFileUrls">
            <!-- 合同是业务凭据，不属于运营素材，一律直接上传，不走素材库 -->
            <UploadFile
              v-model="formData.contractFileUrls"
              :use-material-library="false"
              class="min-w-80px"
            />
            <div class="ml-10px text-12px text-[#999] leading-normal">
              请上传合同电子档 pdf，以及合同纸质扫描件
            </div>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="24">
          <el-form-item label="合同附件上传" prop="attachmentUrls">
            <UploadFile
              v-model="formData.attachmentUrls"
              :use-material-library="false"
              class="min-w-80px"
            />
            <div class="ml-10px text-12px text-[#999] leading-normal">
              请上传合同附件，此处不要上传合同。合同附件主要是：需求说明、功能清单
            </div>
          </el-form-item>
        </el-col>
      </el-row>

      <!-- ========== 第 3 组：产品清单（保留原有能力，原型未覆盖） ========== -->
      <ContentWrap>
        <el-tabs v-model="subTabsName" class="-mt-15px -mb-10px">
          <el-tab-pane label="产品清单" name="product">
            <ContractProductForm
              ref="productFormRef"
              :products="formData.products"
              :disabled="disabled"
            />
          </el-tab-pane>
        </el-tabs>
      </ContentWrap>
      <el-row>
        <el-col :span="8">
          <el-form-item label="产品总金额" prop="totalProductPrice">
            <el-input
              disabled
              v-model="formData.totalProductPrice"
              :formatter="erpPriceInputFormatter"
            />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="整单折扣（%）" prop="discountPercent">
            <el-input-number
              v-model="formData.discountPercent"
              placeholder="请输入整单折扣"
              controls-position="right"
              :min="0"
              :precision="2"
              class="!w-1/1"
            />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <!-- ★ 合同金额（对应原型的「合同金额」列）：
               有产品明细 ⇒ 由「产品总金额 − 折扣」算出，禁用；
               无产品明细 ⇒ **允许手工填写**（用于服务合同等无明细场景）。
               决策见 sql/2026-09-21/crm-contract-extra-fields.sql 文件头第 3 条。 -->
          <el-form-item label="合同金额（元）" prop="totalPrice">
            <el-input-number
              v-model="formData.totalPrice"
              :disabled="hasProducts || disabled"
              :min="0"
              :precision="2"
              controls-position="right"
              placeholder="无产品明细时可手工填写"
              class="!w-1/1"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <!-- ========== 第 4 组：签约信息 ========== -->
      <el-row>
        <el-col :span="8">
          <el-form-item label="公司签约人" prop="signUserId">
            <el-select v-model="formData.signUserId" class="w-1/1">
              <el-option
                v-for="item in userOptions"
                :key="item.id"
                :label="item.nickname"
                :value="item.id!"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="客户签约人" prop="signContactId">
            <el-select
              v-model="formData.signContactId"
              :disabled="!formData.customerId"
              class="w-1/1"
            >
              <el-option
                v-for="item in getContactOptions"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="备注" prop="remark">
            <el-input v-model="formData.remark" placeholder="请输入备注" type="textarea" />
          </el-form-item>
        </el-col>
      </el-row>

      <!-- ========== 第 5 组：说明类长文本（原型为整行） ========== -->
      <el-row>
        <el-col :span="24">
          <el-form-item label="服务说明" prop="serviceDesc">
            <el-input
              v-model="formData.serviceDesc"
              type="textarea"
              :rows="3"
              placeholder="请输入服务说明"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="24">
          <el-form-item label="合同内容" prop="contractContent">
            <el-input
              v-model="formData.contractContent"
              type="textarea"
              :rows="3"
              placeholder="请输入合同内容"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="24">
          <!-- ⚠️「说明」与上面的「备注」(remark) 语义不同，两者并存，不要合并 -->
          <el-form-item label="说明" prop="description">
            <el-input
              v-model="formData.description"
              type="textarea"
              :rows="3"
              placeholder="请输入说明"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="24">
          <el-form-item label="相关文件" prop="relatedFiles">
            <UploadFile
              v-model="formData.relatedFiles"
              :use-material-library="false"
              class="min-w-80px"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="24">
          <el-form-item label="抄送给" prop="ccUserIds">
            <el-select v-model="formData.ccUserIds" multiple filterable class="w-1/1">
              <el-option
                v-for="item in userOptions"
                :key="item.id"
                :label="item.nickname"
                :value="item.id!"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
    <template #footer>
      <el-button :disabled="formLoading" type="primary" @click="submitForm">保存</el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>
<script lang="ts" setup>
import * as CustomerApi from '@/api/crm/customer'
import * as ContractApi from '@/api/crm/contract'
import * as UserApi from '@/api/system/user'
import * as ContactApi from '@/api/crm/contact'
import * as BusinessApi from '@/api/crm/business'
import { erpPriceMultiply, erpPriceInputFormatter } from '@/utils'
import { getCurrentUserId } from '@/utils/auth'
import { notPureNumber } from '@/utils/formRules'
import { DICT_TYPE, getIntDictOptions, getStrDictOptions } from '@/utils/dict'
import ContractProductForm from '@/views/crm/contract/components/ContractProductForm.vue'

type ContractProduct = NonNullable<ContractApi.ContractVO['products']>[number]

const { t } = useI18n() // 国际化
const message = useMessage() // 消息弹窗

const dialogVisible = ref(false) // 弹窗的是否展示
const dialogTitle = ref('') // 弹窗的标题
const formLoading = ref(false) // 表单的加载中：1）修改时的数据加载；2）提交的按钮禁用
const formType = ref('') // 表单的类型：create - 新增；update - 修改
const formData = ref({
  id: undefined as number | undefined,
  no: undefined as string | undefined,
  name: undefined as string | undefined,
  customerId: undefined as number | undefined,
  businessId: undefined as number | undefined,
  orderDate: undefined,
  startTime: undefined,
  endTime: undefined,
  signUserId: undefined as number | undefined,
  signContactId: undefined as number | undefined,
  ownerUserId: undefined as number | undefined,
  discountPercent: 0,
  totalProductPrice: undefined as number | undefined,
  totalPrice: undefined as number | undefined,
  remark: undefined as string | undefined,
  // ===== 2026-09-21 按原型补充的字段 =====
  orderNo: undefined as string | undefined,
  contractType: undefined as string | undefined,
  projectName: undefined as string | undefined,
  projectType: undefined as string | undefined,
  signDate: undefined,
  paymentDirection: undefined as number | undefined,
  renewDesc: undefined as string | undefined,
  renewFee: undefined as number | undefined,
  contractFileUrls: undefined as string | undefined,
  attachmentUrls: undefined as string | undefined,
  paymentMethod: undefined as string | undefined,
  installment: undefined as string | undefined,
  serviceDesc: undefined as string | undefined,
  contractContent: undefined as string | undefined,
  description: undefined as string | undefined,
  relatedFiles: undefined as string | undefined,
  ccUserIds: [] as number[],
  products: [] as ContractProduct[]
})
const isCreate = computed(() => formType.value === 'create') // 是否新增（用于必填分流）
const disabled = computed(() => formType.value === 'detail')
const hasProducts = computed(() => (formData.value.products?.length ?? 0) > 0) // 是否有产品明细
/**
 * 表单校验规则
 *
 * ★ 必填按 `formType` 分流（决策见 sql/2026-09-21/crm-contract-extra-fields.sql 文件头第 8 条）：
 *   - **新增**：严格对齐原型表单的 `*`；
 *   - **编辑**：新增字段一律放行 —— 存量的 2 条合同（id=3 / id=5）这些字段全是空的，
 *     一旦编辑态也卡必填，用户点「编辑」保存就被拦住。
 *   - 后端 `CrmContractSaveReqVO` 对这些字段**没有** `@NotNull`，所以不会被后端打回。
 */
const formRules = computed(() => ({
  name: [
    { required: true, message: '合同名称不能为空', trigger: 'blur' },
    notPureNumber('合同名称')
  ],
  customerId: [{ required: true, message: '客户不能为空', trigger: 'blur' }],
  orderDate: [{ required: true, message: '下单日期不能为空', trigger: 'blur' }],
  ownerUserId: [{ required: true, message: '负责人不能为空', trigger: 'blur' }],
  orderNo: isCreate.value ? [{ required: true, message: '订单编号不能为空', trigger: 'blur' }] : [],
  contractType: isCreate.value
    ? [{ required: true, message: '合同类型不能为空', trigger: 'change' }]
    : [],
  projectName: isCreate.value
    ? [{ required: true, message: '项目名称不能为空', trigger: 'blur' }]
    : [],
  signDate: isCreate.value
    ? [{ required: true, message: '签约日期不能为空', trigger: 'change' }]
    : [],
  paymentDirection: isCreate.value
    ? [{ required: true, message: '收/付款不能为空', trigger: 'change' }]
    : [],
  totalPrice: isCreate.value
    ? [{ required: true, message: '合同金额不能为空', trigger: 'change' }]
    : [],
  contractFileUrls: isCreate.value
    ? [{ required: true, message: '请上传合同', trigger: 'change' }]
    : [],
  attachmentUrls: isCreate.value
    ? [{ required: true, message: '请上传合同附件', trigger: 'change' }]
    : []
}))
const formRef = ref() // 表单 Ref
const userOptions = ref<UserApi.UserVO[]>([]) // 用户列表
const customerList = ref<CustomerApi.CustomerVO[]>([]) // 客户列表的数据
const businessList = ref<BusinessApi.BusinessVO[]>([])
const contactList = ref<ContactApi.ContactVO[]>([])

/** 子表的表单 */
const subTabsName = ref('product')
const productFormRef = ref()

/** 计算 discountPrice、totalPrice 价格 */
watch(
  () => formData.value,
  (val) => {
    if (!val) {
      return
    }
    const totalProductPrice = (val.products ?? []).reduce((prev, curr) => prev + curr.totalPrice, 0)
    const discountPrice =
      val.discountPercent != null
        ? (erpPriceMultiply(totalProductPrice, (val.discountPercent ?? 0) / 100.0) ?? 0)
        : 0
    // 赋值
    formData.value.totalProductPrice = totalProductPrice
    // ★ 有产品明细 ⇒ 合同金额由明细算出（覆盖手填值）；
    //   无产品明细 ⇒ **保留手填值**，不再强制归零（原实现会把手填的金额冲掉）。
    if ((val.products?.length ?? 0) > 0) {
      formData.value.totalPrice = totalProductPrice - discountPrice
    }
  },
  { deep: true }
)

/** 打开弹窗 */
const open = async (type: string, id?: number) => {
  dialogVisible.value = true
  dialogTitle.value = t('action.' + type)
  formType.value = type
  resetForm()
  // 修改时，设置数据
  if (id) {
    formLoading.value = true
    try {
      formData.value = await ContractApi.getContract(id)
    } finally {
      formLoading.value = false
    }
  }
  // 获得客户列表
  customerList.value = await CustomerApi.getCustomerSimpleList()
  // 获得用户列表
  userOptions.value = await UserApi.getSimpleUserList()
  // 默认新建时选中自己
  if (formType.value === 'create') {
    formData.value.ownerUserId = getCurrentUserId()
  }
  // 获取联系人
  contactList.value = await ContactApi.getSimpleContactList()
  // 获得商机列表
  businessList.value = await BusinessApi.getSimpleBusinessList()
}
defineExpose({ open }) // 提供 open 方法，用于打开弹窗

/** 提交表单 */
const emit = defineEmits(['success']) // 定义 success 事件，用于操作成功后的回调
const submitForm = async () => {
  // 校验表单
  if (!formRef) return
  const valid = await formRef.value.validate()
  if (!valid) return
  // 提交请求
  formLoading.value = true
  productFormRef.value.validate()
  try {
    const data = unref(formData.value) as unknown as ContractApi.ContractVO
    if (formType.value === 'create') {
      await ContractApi.createContract(data)
      message.success(t('common.createSuccess'))
    } else {
      await ContractApi.updateContract(data)
      message.success(t('common.updateSuccess'))
    }
    dialogVisible.value = false
    // 发送操作成功的事件
    emit('success')
  } finally {
    formLoading.value = false
  }
}

/** 重置表单 */
const resetForm = () => {
  formData.value = {
    id: undefined,
    no: undefined,
    name: undefined,
    customerId: undefined,
    businessId: undefined,
    orderDate: undefined,
    startTime: undefined,
    endTime: undefined,
    signUserId: undefined,
    signContactId: undefined,
    ownerUserId: undefined,
    discountPercent: 0,
    totalProductPrice: undefined,
    totalPrice: undefined,
    remark: undefined,
    // ===== 2026-09-21 按原型补充的字段 =====
    orderNo: undefined,
    contractType: undefined,
    projectName: undefined,
    projectType: undefined,
    signDate: undefined,
    paymentDirection: undefined,
    renewDesc: undefined,
    renewFee: undefined,
    contractFileUrls: undefined,
    attachmentUrls: undefined,
    paymentMethod: undefined,
    installment: undefined,
    serviceDesc: undefined,
    contractContent: undefined,
    description: undefined,
    relatedFiles: undefined,
    ccUserIds: [],
    products: []
  }
  formRef.value?.resetFields()
}

/** 处理切换客户 */
const handleCustomerChange = () => {
  formData.value.businessId = undefined
  formData.value.signContactId = undefined
  formData.value.products = []
}

/** 处理商机变化 */
const handleBusinessChange = async (businessId: number) => {
  const business = await BusinessApi.getBusiness(businessId)
  business.products.forEach((item) => {
    item.contractPrice = item.businessPrice
  })
  formData.value.products = business.products as unknown as ContractProduct[]
}

/** 动态获取客户联系人 */
const getContactOptions = computed(() =>
  contactList.value.filter((item) => item.customerId == formData.value.customerId)
)
/** 动态获取商机 */
const getBusinessOptions = computed(() =>
  businessList.value.filter((item) => item.customerId == formData.value.customerId)
)
</script>
